import { ArrowCircleUp2, Trash } from 'iconsax-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupArrow from '@/assets/images/icons/popup-arrow.svg?react';
import useAudioRecorder from '@/hooks/conversations/use-audio-recorder';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useMediaSupport from '@/hooks/settings/use-media-support';
import { convertBlobToUploadable } from '@/models/uploadable';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import { formatSecond } from '@/utils/second-to-time-string';

interface Props {
  isLoading?: boolean;
}

export default function ConversationRecordForm({ isLoading }: Props) {
  const { t } = useTranslation();
  const { handleSubmit } = useConversationHandlers();
  const { toggleRecorderOpen } = useConversationForm();
  const { recordableMediaType } = useMediaSupport();

  const { isRecording, duration, canRecord, stopRecording, startRecording, cancelRecording } = useAudioRecorder(
    (blob) => {
      handleSubmit({
        AttachmentType: FileType.Audio,
        Attachment: [convertBlobToUploadable(blob)],
        Content: duration.toString(),
      })
        .then(() => toggleRecorderOpen())
        .catch(console.warn);
    },
    {
      audioTrackConstraints: {
        channelCount: 1,
        noiseSuppression: true,
        echoCancellation: true,
      },
      mediaRecorderOptions: {
        mimeType: recordableMediaType ?? 'audio/webm;codecs=opus',
      },
    },
  );
  const handleButtonClick = () => {
    if (isRecording) {
      stopRecording().catch(console.warn);
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    startRecording();
  }, []);

  return (
    <div
      onContextMenu={(e) => e.preventDefault()}
      className='flex flex-1 items-center justify-between gap-1'>
      <button
        type='button'
        className='btn btn-sm btn-circle btn-ghost'
        onClick={() => {
          if (isRecording) cancelRecording();
          toggleRecorderOpen();
        }}>
        <Trash className='size-6' />
      </button>

      {canRecord ? (
        <div className='indicator relative size-full flex-1 select-none justify-end'>
          <div className='flex w-full items-center justify-center gap-2'>
            {isLoading ? (
              <span className='loading loading-spinner loading-sm' />
            ) : (
              <>
                <span
                  className={cn('size-1.5 rounded-full bg-neutral-400 transition-colors', {
                    'bg-error text-error/50 animate-record-pulse': isRecording,
                  })}
                />
                {formatSecond(duration)}
              </>
            )}
          </div>
        </div>
      ) : (
        <p className='text-sm text-center text-error'>{t('errors.no-microphone')}</p>
      )}

      <div className={cn('flex items-center', { hidden: isLoading })}>
        <button
          type='button'
          onClick={handleButtonClick}
          className='relative mx-2 min-w-max rounded bg-base-200 px-3 py-2 text-h3 desktop:text-xs'>
          {t('conversation.recording.send')}
          <PopupArrow className='absolute -right-3 top-1/2 size-5 -translate-y-1/2 -rotate-90 text-base-200' />
        </button>

        <button
          type='button'
          className={cn('btn btn-circle btn-sm p-0 btn-ghost transition-transform text-primary', {
            'rotate-90': !isRecording,
          })}
          onClick={handleButtonClick}>
          <ArrowCircleUp2
            variant='Bold'
            className='size-8'
          />
        </button>
      </div>
    </div>
  );
}
